import PopupDialog from 'src/SharedComponents/PopupDialog/PopupDialog';
import { useEffect, useMemo, useState } from 'react';
import { useLicencePayment } from 'src/Utils/Hooks/Payments/usePayment';
import { InfoMessageType } from 'src/Components/InfoBar/InfoBar';
import SubscribePlan from './SubscribePlan/SubscribePlan';
import { AuraProductLicenseType, IPaymentProducts } from 'src/Utils/Hooks/Payments/useGetPaymentProducts';
import { useTranslation } from 'react-i18next';
import PillTabs, { Tab } from 'src/SharedComponents/PillTabs/PillTabs';
import { calculateMaxSavings } from 'src/Utils/CalculateSavings/CalculateSavings';
import { useClinicContext } from 'src/Utils/Hooks/ClinicContext';
import OwnerPermissionNote from './OwnerPermissionNote/OwnerPermissionNote';

import './PaymentDialog.css';

interface ILicensePayment{
    onClose: () => void;
    clinicId: string;
    products: IPaymentProducts;
    updatePageLoader: (load: boolean) => void;
    setInfo: (message: string, type: InfoMessageType) => void;
}

export const Monthly = 'Monthly billed';
export const Annually = 'Annually billed';

const LicensePayment: React.FC<ILicensePayment> = ({onClose,products, clinicId , updatePageLoader, setInfo}) => {
    const { t } = useTranslation();
    const { currentUser } = useClinicContext();
    // Only an Owner can complete a purchase, so everyone else sees the plans
    // read-only with the restriction called out as an error notice.
    const isClinicOwner = currentUser?.isClinicOwner ?? false;
    const [selectedTab, setSelectedTab] = useState<string>(Monthly); // Default selected tab

    const handleTabChange = (tab: string) => {
        setSelectedTab(tab); // Update the selected tab when a new tab is clicked
    };

    const { checkoutUrl, createLicensePaymentPage, reqestLoading, reqestError} = useLicencePayment();
    const tabs: Tab[] = useMemo(() => [
            { title: t(Monthly), disabled: false },
            { title: t(Annually), disabled: false }
        ], [t]);
    

    const handlePayClick = (priceId: string) => {
        createLicensePaymentPage(priceId, clinicId);
    };

    useEffect(() => {
        updatePageLoader(reqestLoading) 
    }, [reqestLoading, updatePageLoader]);

    useEffect(() => {
        if (checkoutUrl) {
            window.open(checkoutUrl, '_self');
            onClose()
        }
    }, [checkoutUrl, onClose]);

    useEffect(() => {
        if (reqestError !== '') {
            setInfo(reqestError, InfoMessageType.error);
            onClose()   
        } 
    },[reqestError, setInfo, onClose])

    const maxSavingsPercentage = calculateMaxSavings(products);
    
    return(
        <div>
            <PopupDialog onClose={() => onClose()} marginTop="80px" popupWidth="800px" surfaceClassName="payment-dialog__surface">
                <div className="p-3">
                    <div className="row" style={{padding:"0 0 10px 10px"}}>
                            <div className="col-md-10">
                                <span className="payment-dialog__title">{t('ChooseOption')}</span>
                            </div>
                    </div>
                    <br/>
                    <PillTabs
                        tabs={tabs}
                        selectedTab={selectedTab}
                        onTabChange={handleTabChange}
                        bottomTextIndex={1}
                        bottomText={t('SaveUpTo', { percentage: maxSavingsPercentage })}
                    />
                    <div className="row pb-4 pt-5" style={{margin: 'auto'}}>
                        {products.suiteProductComboInfos?.map((suite, index) => (
                            <SubscribePlan
                                key={suite.productType}
                                selectedTab={selectedTab}
                                title={AuraProductLicenseType[suite.productType]}
                                currency={suite.currency}
                                userSeats={suite.userSeats}
                                storageInGb={suite.storageInGb}
                                monthlypriceObj={suite.monthlyInfo}
                                yearlypriceObj={suite.yearlyInfo}
                                onPayClick={handlePayClick}
                                disabled={!isClinicOwner}
                            />
                        ))}
                    </div>
                    <div className="payment-dialog__footer">
                        <OwnerPermissionNote />
                        <span className="payment-dialog__contact">
                            {t('FurtherAssistance')}&nbsp;
                            <a href="mailto:office@aurareality.com" className="text-decoration-underline" style={{ color: 'var(--color-primary)' }}>
                                {t('AuraOffice')}
                            </a>
                        </span>
                    </div>
                </div>
            </PopupDialog>
        </div>
    );
}

export default LicensePayment;